/**
 * OpsMyTasksPage — a member's personal task inbox.
 * Route: /ops/my-tasks
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { opsApi, useOpsAuth } from './OpsAuthContext';
import OpsLayout from './OpsLayout';
import { TASK_STATUS, TASK_STATUS_ORDER, fmtDate } from './opsConstants';

const OpsMyTasksPage = () => {
  const { user } = useOpsAuth();
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter === 'all' ? '' : `?status=${filter}`;
      const { data } = await opsApi.get(`/tasks/mine${q}`);
      setTasks(data);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const updateStatus = async (task, status) => {
    try {
      await opsApi.patch(`/projects/${task.project_id}/tasks/${task.id}`, { status });
      refresh();
    } catch {
      /* ignore */
    }
  };

  const counts = tasks.reduce((acc, t) => {
    acc[t.status] = (acc[t.status] || 0) + 1;
    return acc;
  }, { total: tasks.length });

  return (
    <OpsLayout title="مهامي" subtitle={`المكلَّف: ${user.name_ar}`}>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        <button
          data-testid="mytasks-filter-all"
          onClick={() => setFilter('all')}
          className={`text-[12px] font-['Noto_Kufi_Arabic'] px-4 py-2 rounded-lg border transition ${
            filter === 'all'
              ? 'bg-[#1F3D2B] text-white border-[#1F3D2B]'
              : 'bg-white text-[#1F3D2B] border-[#E6E0D6]'
          }`}
        >
          الكل ({counts.total || 0})
        </button>
        {TASK_STATUS_ORDER.map((s) => (
          <button
            key={s}
            data-testid={`mytasks-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`text-[12px] font-['Noto_Kufi_Arabic'] px-4 py-2 rounded-lg border transition ${
              filter === s
                ? 'bg-[#1F3D2B] text-white border-[#1F3D2B]'
                : 'bg-white text-[#1F3D2B] border-[#E6E0D6]'
            }`}
          >
            {TASK_STATUS[s].label} ({counts[s] || 0})
          </button>
        ))}
      </div>

      {loading && <p className="text-[13px] text-[#7A866E] font-['Noto_Kufi_Arabic']">جارٍ التحميل…</p>}
      {!loading && tasks.length === 0 && (
        <div className="bg-white rounded-2xl ring-1 ring-[#E6E0D6] p-10 text-center">
          <p className="font-['Noto_Kufi_Arabic'] text-[14px] text-[#7A866E]">
            لا توجد مهام مطابقة للفلتر الحالي
          </p>
        </div>
      )}

      <ul className="grid gap-3">
        {tasks.map((t) => (
          <li
            key={t.id}
            data-testid={`mytask-${t.id}`}
            className="bg-white rounded-2xl ring-1 ring-[#E6E0D6] p-5"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                {t.project && (
                  <Link
                    to={`/ops/projects/${t.project_id}`}
                    className="inline-block text-[11px] font-['Playfair_Display'] text-[#4A5D23] mb-1 hover:underline"
                  >
                    {t.project.code} · {t.project.client_name}
                  </Link>
                )}
                <p className="font-['Noto_Kufi_Arabic'] text-[15px] font-bold text-[#1F3D2B]">
                  {t.title}
                </p>
                {t.description && (
                  <p className="font-['Noto_Kufi_Arabic'] text-[12px] text-[#5C6D49] mt-1 leading-7">
                    {t.description}
                  </p>
                )}
                <p className="font-['Noto_Kufi_Arabic'] text-[11px] text-[#7A866E] mt-1">
                  أُنشئت {fmtDate(t.created_at)}
                </p>
              </div>
              <select
                data-testid={`mytask-status-${t.id}`}
                value={t.status}
                onChange={(e) => updateStatus(t, e.target.value)}
                className={`text-[11px] font-['Noto_Kufi_Arabic'] rounded-full px-3 py-1 border-0 ${TASK_STATUS[t.status]?.color || ''}`}
              >
                {TASK_STATUS_ORDER.map((s) => (
                  <option key={s} value={s}>{TASK_STATUS[s].label}</option>
                ))}
              </select>
            </div>
          </li>
        ))}
      </ul>
    </OpsLayout>
  );
};

export default OpsMyTasksPage;
